import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Image,
  Switch,
  Alert,
  Dimensions,
} from 'react-native';
import { FuelGauge } from '../vehicle/FuelGauge';
import { VehicleDamagePicker } from '../vehicle/VehicleDamagePicker';
import { SignatureCanvas, SignatureDisplay } from '../ui/SignatureCanvas';
import { Vehicle, FormData, VehicleDamage } from '../../types';
import { useAuthStore } from '../../stores/authStore';

const { width: screenWidth } = Dimensions.get('window');

interface JobCardFormA4Props {
  formData: FormData;
  onInputChange: (field: keyof FormData, value: string | boolean | number) => void;
  fuelLevel: number;
  onFuelLevelChange: (level: number) => void;
  onKeyCheckboxChange: (selectedKey: 'yes' | 'no') => void;
  vehicleDatabase: Vehicle[];
  onVehicleSelect: (vehicle: Vehicle) => void;
  vehicleDamages: VehicleDamage[];
  onVehicleDamagesChange: (damages: VehicleDamage[]) => void;
  maintenanceSignatureSvg: string;
  onMaintenanceSignatureChange: (signature: string) => void;
  workshopSignatureSvg: string;
  onWorkshopSignatureChange: (signature: string) => void;
  readOnly?: boolean;
}

// Constants
const LOGO_URL = 'https://ik.imagekit.io/to2pwwjig/Logo.jpg?updatedAt=1750740262665';
const VEHICLE_DIAGRAM_URL = 'https://ik.imagekit.io/to2pwwjig/Tesla%20Model%20S%20-%20Free%20CAD%20Drawings.jpeg?updatedAt=1750739302834';

// Utility function to format vehicle plate
const formatVehiclePlate = (vehicle: Vehicle): string => {
  if (!vehicle.licensePlate) return 'N/A';
  
  const plate = vehicle.licensePlate || '';
  const code = vehicle.vehicleCode || '1';
  
  return `${code}-${plate}`;
};

export const JobCardFormA4: React.FC<JobCardFormA4Props> = ({
  formData,
  onInputChange,
  fuelLevel,
  onFuelLevelChange,
  onKeyCheckboxChange,
  vehicleDatabase,
  onVehicleSelect,
  vehicleDamages,
  onVehicleDamagesChange,
  maintenanceSignatureSvg,
  onMaintenanceSignatureChange,
  workshopSignatureSvg,
  onWorkshopSignatureChange,
  readOnly = false,
}) => {
  const { user } = useAuthStore();
  const [suggestions, setSuggestions] = useState<Vehicle[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [showMaintenanceSignature, setShowMaintenanceSignature] = useState(false);
  const [showWorkshopSignature, setShowWorkshopSignature] = useState(false);

  const handleLicensePlateChange = (value: string) => {
    onInputChange('licensePlate', value);

    if (value.length > 0) {
      const filteredSuggestions = vehicleDatabase.filter(vehicle => {
        const plate = formatVehiclePlate(vehicle);
        return plate.toLowerCase().includes(value.toLowerCase());
      });
      setSuggestions(filteredSuggestions);
      setShowSuggestions(filteredSuggestions.length > 0);
      setActiveSuggestionIndex(0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionPress = (vehicle: Vehicle) => {
    onVehicleSelect(vehicle);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const workTypes = [
    { id: 'underMaintenance', label: 'Under Maintenance' },
    { id: 'accidentRepair', label: 'Accident Repair' },
    { id: 'underMaintenanceAlQouz', label: 'Under Maintenance - Al Qouz' },
    { id: 'underMaintenanceAgency', label: 'Under Maintenance - Agency' },
    { id: 'others', label: 'Others' },
  ];

  const conditionChecks = [
    { id: 'lightsOk', label: 'Lights OK' },
    { id: 'wipersOk', label: 'Wipers OK' },
    { id: 'hornOk', label: 'Horn OK' },
    { id: 'acOk', label: 'A/C OK' },
    { id: 'tiresOk', label: 'Tires OK' },
    { id: 'mirrorsOk', label: 'Mirrors OK' },
  ];

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.formContainer} pointerEvents={readOnly ? 'none' : 'auto'}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoArea}>
            <Image source={{ uri: LOGO_URL }} style={styles.logo} />
            <Text style={styles.cardTitle}>Internal STSC Management Job Card</Text>
          </View>
          <View style={styles.jobNumberArea}>
            <TextInput
              style={[styles.jobNumberInput, styles.jobNumberReadOnly]}
              value={formData.jobNumber}
              onChangeText={(value) => onInputChange('jobNumber', value)}
              placeholder="Auto-generated..."
              placeholderTextColor="#ccc"
              editable={false}
            />
          </View>
        </View>

        {/* Top sections container */}
        <View style={styles.topSectionsContainer}>
          {/* Request Information Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Request Information</Text>
            <View style={styles.formRow}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Request Date:</Text>
                <TextInput
                  style={styles.input}
                  value={formData.requestDate}
                  onChangeText={(value) => onInputChange('requestDate', value)}
                  placeholder="YYYY-MM-DD"
                />
              </View>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Required By:</Text>
                <TextInput
                  style={styles.input}
                  value={formData.requiredBy}
                  onChangeText={(value) => onInputChange('requiredBy', value)}
                  placeholder="YYYY-MM-DD"
                />
              </View>
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Requested By:</Text>
              <TextInput
                style={[styles.input, styles.lockedInput]}
                value={formData.requestedBy || user?.name || user?.email || 'Unknown User'}
                editable={false}
                placeholder="Person name"
              />
            </View>
          </View>

          {/* Vehicle Information Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Vehicle Information</Text>
            <View style={styles.formRow}>
              <View style={[styles.formGroup, { position: 'relative' }]}>
                <Text style={styles.label}>License Plate:</Text>
                <TextInput
                  style={styles.input}
                  value={formData.licensePlate}
                  onChangeText={handleLicensePlateChange}
                  placeholder="Enter license plate"
                  autoCapitalize="characters"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <View style={styles.suggestionsContainer}>
                    {suggestions.map((vehicle, index) => (
                      <TouchableOpacity
                        key={`${vehicle.licensePlate}-${index}`}
                        style={[
                          styles.suggestionItem,
                          index === activeSuggestionIndex && styles.activeSuggestion
                        ]}
                        onPress={() => handleSuggestionPress(vehicle)}
                      >
                        <Text style={styles.suggestionPlate}>{formatVehiclePlate(vehicle)}</Text>
                        <Text style={styles.suggestionDetails}>
                          {vehicle.makeModel || ''}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Make & Model:</Text>
                <TextInput
                  style={styles.input}
                  value={formData.makeModel}
                  onChangeText={(value) => onInputChange('makeModel', value)}
                  placeholder="Vehicle make & model"
                />
              </View>
            </View>
            <View style={styles.formRow}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Year:</Text>
                <TextInput
                  style={styles.input}
                  value={formData.year}
                  onChangeText={(value) => onInputChange('year', value)}
                  placeholder="2023"
                  keyboardType="numeric"
                />
              </View>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Current Mileage:</Text>
                <TextInput
                  style={styles.input}
                  value={formData.mileage}
                  onChangeText={(value) => onInputChange('mileage', value)}
                  placeholder="Mileage"
                  keyboardType="numeric"
                />
              </View>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Assigned Driver:</Text>
                <TextInput
                  style={styles.input}
                  value={formData.driver || ''}
                  onChangeText={(value) => onInputChange('driver', value)}
                  placeholder="Driver name"
                />
              </View>
            </View>
            <View style={styles.formRow}>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Fuel Level:</Text>
                <FuelGauge
                  fuelLevel={fuelLevel}
                  onFuelLevelChange={onFuelLevelChange}
                />
              </View>
              <View style={styles.formGroup}>
                <Text style={styles.label}>Vehicle Keys:</Text>
                <View style={styles.checkboxGroup}>
                  <TouchableOpacity
                    style={styles.checkboxItem}
                    onPress={() => onKeyCheckboxChange('yes')}
                  >
                    <View style={[styles.checkbox, formData.keysYes && styles.checkboxChecked]}>
                      {formData.keysYes && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkboxLabel}>Yes</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.checkboxItem}
                    onPress={() => onKeyCheckboxChange('no')}
                  >
                    <View style={[styles.checkbox, formData.keysNo && styles.checkboxChecked]}>
                      {formData.keysNo && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkboxLabel}>No</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Vehicle Condition Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Vehicle Condition</Text>
          <View style={styles.vehicleConditionLayout}>
            <View style={styles.vehicleDiagramContainer}>
              <VehicleDamagePicker
                damages={vehicleDamages}
                onDamagesChange={onVehicleDamagesChange}
                editable={!readOnly}
                containerWidth={420}
                containerHeight={330}
              />
            </View>
            <View style={styles.vehicleConditionDetails}>
              {/* Type of Work - Moved Here */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Type of Work:</Text>
                <View style={styles.workTypesGrid}>
                  {workTypes.map((workType) => (
                    <TouchableOpacity
                      key={workType.id}
                      style={styles.checkboxItem}
                      onPress={() => onInputChange(workType.id as keyof FormData, !(formData[workType.id as keyof FormData] as boolean))}
                    >
                      <View style={[styles.checkbox, (formData[workType.id as keyof FormData] as boolean) && styles.checkboxChecked]}>
                        {(formData[workType.id as keyof FormData] as boolean) && <Text style={styles.checkmark}>✓</Text>}
                      </View>
                      <Text style={styles.checkboxLabel}>{workType.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              
              <View style={styles.formGroup}>
                <Text style={styles.label}>Condition Notes:</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={formData.conditionNotes || ''}
                  onChangeText={(value) => onInputChange('conditionNotes', value)}
                  placeholder="Enter any damage or condition notes here..."
                  multiline
                  numberOfLines={3}
                  textAlignVertical="top"
                />
              </View>
              <View style={styles.conditionChecksGrid}>
                {conditionChecks.map((check) => (
                  <TouchableOpacity
                    key={check.id}
                    style={styles.checkboxItem}
                    onPress={() => onInputChange(check.id as keyof FormData, !(formData[check.id as keyof FormData] as boolean))}
                  >
                    <View style={[styles.checkbox, (formData[check.id as keyof FormData] as boolean) && styles.checkboxChecked]}>
                      {(formData[check.id as keyof FormData] as boolean) && <Text style={styles.checkmark}>✓</Text>}
                    </View>
                    <Text style={styles.checkboxLabel}>{check.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* Work Required Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Work Required</Text>
          <View style={styles.formGroup}>
            <Text style={styles.label}>Description of Work Required:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.workDescription || ''}
              onChangeText={(value) => onInputChange('workDescription', value)}
              placeholder="Describe the work that needs to be done..."
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>
        </View>

        {/* Handover Information Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Handover Information</Text>
          <View style={styles.formRow}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Handover Date:</Text>
              <TextInput
                style={styles.input}
                value={formData.handoverDate || ''}
                onChangeText={(value) => onInputChange('handoverDate', value)}
                placeholder="YYYY-MM-DD"
              />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Expected Return Date:</Text>
              <TextInput
                style={styles.input}
                value={formData.returnDate || ''}
                onChangeText={(value) => onInputChange('returnDate', value)}
                placeholder="YYYY-MM-DD"
              />
            </View>
          </View>
          <View style={styles.formRow}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Handed Over By:</Text>
              <TextInput
                style={styles.input}
                value={formData.handedBy || ''}
                onChangeText={(value) => onInputChange('handedBy', value)}
                placeholder="Person name"
              />
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Received By (Workshop):</Text>
              <TextInput
                style={styles.input}
                value={formData.receivedBy || ''}
                onChangeText={(value) => onInputChange('receivedBy', value)}
                placeholder="Workshop person"
              />
            </View>
          </View>
          <View style={styles.formRow}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Maintenance Dept. Signature:</Text>
              {maintenanceSignatureSvg ? (
                <View>
                  <SignatureDisplay 
                    signature={maintenanceSignatureSvg}
                    onEdit={() => setShowMaintenanceSignature(true)}
                  />
                </View>
              ) : formData.maintenanceSignature ? (
                // Show legacy text signature if no SVG signature exists
                <View>
                  <Text style={styles.legacySignature}>{formData.maintenanceSignature}</Text>
                  <TouchableOpacity 
                    style={styles.signatureButton}
                    onPress={() => setShowMaintenanceSignature(true)}
                  >
                    <Text style={styles.signatureButtonText}>Add Canvas Signature</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity 
                  style={styles.signatureButton}
                  onPress={() => setShowMaintenanceSignature(true)}
                >
                  <Text style={styles.signatureButtonText}>Add Signature</Text>
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Workshop Signature:</Text>
              {workshopSignatureSvg ? (
                <View>
                  <SignatureDisplay 
                    signature={workshopSignatureSvg}
                    onEdit={() => setShowWorkshopSignature(true)}
                  />
                </View>
              ) : formData.workshopSignature ? (
                // Show legacy text signature if no SVG signature exists
                <View>
                  <Text style={styles.legacySignature}>{formData.workshopSignature}</Text>
                  <TouchableOpacity 
                    style={styles.signatureButton}
                    onPress={() => setShowWorkshopSignature(true)}
                  >
                    <Text style={styles.signatureButtonText}>Add Canvas Signature</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity 
                  style={styles.signatureButton}
                  onPress={() => setShowWorkshopSignature(true)}
                >
                  <Text style={styles.signatureButtonText}>Add Signature</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>

        {/* Signature Modals */}
        <SignatureCanvas
          visible={showMaintenanceSignature}
          onClose={() => setShowMaintenanceSignature(false)}
          onSave={(signature: string) => {
            onMaintenanceSignatureChange(signature);
            setShowMaintenanceSignature(false);
          }}
          title="Maintenance Department Signature"
        />
        <SignatureCanvas
          visible={showWorkshopSignature}
          onClose={() => setShowWorkshopSignature(false)}
          onSave={(signature: string) => {
            onWorkshopSignatureChange(signature);
            setShowWorkshopSignature(false);
          }}
          title="Workshop Signature"
        />

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            <Text style={styles.footerBold}>Job Card #: {formData.jobNumber || '______'}</Text>
          </Text>
          <Text style={styles.footerText}>
            <Text style={styles.footerBold}>Date Printed:</Text> {new Date().toLocaleDateString()}
          </Text>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  formContainer: {
    padding: 15,
    width: '100%',
    maxWidth: 900,
    alignSelf: 'center',
  },
  header: {
    backgroundColor: '#455A64',
    color: 'white',
    padding: 15,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    borderRadius: 4,
  },
  logoArea: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  logo: {
    height: 40,
    width: 40,
    borderRadius: 3,
    marginRight: 10,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
    flex: 1,
  },
  jobNumberArea: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  jobNumberLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'white',
  },
  jobNumberInput: {
    backgroundColor: 'transparent',
    borderBottomWidth: 2,
    borderBottomColor: 'white',
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    width: 220,
    textAlign: 'left',
    marginLeft: 5,
    paddingHorizontal: 2,
    paddingVertical: 0,
  },
  jobNumberReadOnly: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderBottomColor: 'rgba(255, 255, 255, 0.5)',
  },
  topSectionsContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 6,
  },
  section: {
    borderWidth: 1,
    borderColor: '#ddd',
    padding: 8,
    borderRadius: 4,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#455A64',
    marginBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingBottom: 3,
  },
  formRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 4,
  },
  formGroup: {
    flex: 1,
    marginBottom: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 2,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    height: 22,
    paddingHorizontal: 5,
    paddingVertical: 2,
    fontSize: 12,
    borderRadius: 2,
    backgroundColor: '#fff',
  },
  textArea: {
    height: 60,
    textAlignVertical: 'top',
    paddingTop: 5,
  },
  signatureInput: {
    height: 40,
    fontStyle: 'italic',
  },
  instructionText: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  vehicleConditionLayout: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
  },
  vehicleDiagramContainer: {
    width: 436,
    padding: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 3,
    backgroundColor: '#f9f9f9',
  },
  vehicleDiagram: {
    width: '100%',
    height: 170,
    resizeMode: 'contain',
  },
  vehicleConditionDetails: {
    flex: 1,
    gap: 6,
  },
  conditionChecksGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  workTypesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 6,
    marginTop: 2,
  },
  checkboxGroup: {
    flexDirection: 'row',
    gap: 15,
    marginTop: 2,
  },
  checkboxItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
    marginRight: 6,
    minWidth: 90,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderWidth: 1,
    borderColor: '#546E7A',
    borderRadius: 2,
    marginRight: 6,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: '#546E7A',
  },
  checkmark: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 12,
    color: '#333',
    flexShrink: 0,
  },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    maxHeight: 150,
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    zIndex: 100,
    elevation: 5,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  activeSuggestion: {
    backgroundColor: '#e9ecef',
  },
  suggestionPlate: {
    fontWeight: 'bold',
    color: '#455A64',
    fontSize: 12,
  },
  suggestionDetails: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  footer: {
    marginTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#ddd',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 11,
    color: '#555',
  },
  footerBold: {
    fontWeight: 'bold',
  },
  signatureButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginTop: 5,
    alignItems: 'center',
  },
  signatureButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  legacySignature: {
    padding: 10,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 5,
    backgroundColor: '#f9f9f9',
    fontStyle: 'italic',
    color: '#666',
    marginBottom: 5,
  },
  lockedInput: {
    backgroundColor: '#f5f5f5',
    color: '#666',
  },
});

export default JobCardFormA4;
